import type { ColorId } from './colors.js';

/** Central balancing constants — design doc §5–§15, §21. */

export const DAY_SECONDS = 20;
export const DAYS_PER_WEEK = 7;
export const WEEK_SECONDS = DAY_SECONDS * DAYS_PER_WEEK;

/** Initial playfield: width × height (screen landscape). */
export const INITIAL_GRID_WIDTH = 15;
export const INITIAL_GRID_HEIGHT = 8;

/** @deprecated Use INITIAL_GRID_WIDTH / HEIGHT. */
export const INITIAL_GRID_SIZE = INITIAL_GRID_WIDTH;

/** Grid dims by minimum day. Each expansion: +2 width, +2 height (15×8 → 17×10 → 19×12…). */
export const GRID_EXPANSION_SCHEDULE: readonly {
  fromDay: number;
  width: number;
  height: number;
}[] = [
  { fromDay: 1, width: 15, height: 8 },
  { fromDay: 14, width: 17, height: 10 },
  { fromDay: 28, width: 19, height: 12 },
  { fromDay: 42, width: 21, height: 14 },
  { fromDay: 56, width: 23, height: 16 },
  { fromDay: 70, width: 25, height: 18 },
  { fromDay: 91, width: 27, height: 20 },
  { fromDay: 112, width: 29, height: 22 },
  { fromDay: 140, width: 31, height: 24 },
];

/** After last scheduled entry, +2W/+2H every N days. */
export const GRID_EXPANSION_INTERVAL_AFTER = 28;

export const INITIAL_CABLES = 30;
/** Each map starts with one relay (no slowdown at crossings) and one switch (no network mixing). */
export const INITIAL_RELAYS = 1;
export const INITIAL_SWITCHES = 1;

/** Energy delivered per pulse. With interval 2s → 0.5 production / second. */
export const PULSE_ENERGY = 1;
export const BULB_MAX_ENERGY = 100;

/** All generator sizes pulse once every 2s (= 0.5 energy/s). */
export const GENERATOR_INTERVALS = {
  small: 2,
  medium: 2,
  large: 2,
} as const;

export type GeneratorSize = keyof typeof GENERATOR_INTERVALS;

/** Bulbs drain ≥ 0.75 /s — needs multiple gens per bulb (same ratios as before, ×0.5 pace). */
export const CONSUMPTION_TIERS = [
  { id: 'standard' as const, rate: 0.75, unlockDay: 1 },
  { id: 'intensive' as const, rate: 1, unlockDay: 14 },
  { id: 'powerful' as const, rate: 1.25, unlockDay: 28 },
  { id: 'critical' as const, rate: 1.75, unlockDay: 56 },
] as const;

export type ConsumptionTierId = (typeof CONSUMPTION_TIERS)[number]['id'];

/** Throughput / speed factor lost per distinct foreign color at a cell. */
export const CROSSING_PENALTY = 0.4;
/** Pulse speed multiplier while traversing a color-interference cell. */
export const INTERFERENCE_SPEED_MULT = 0.4;
/** Slight slowdown when two+ pulses occupy the same cell (any colors). */
export const PULSE_MEETING_SPEED_MULT = 0.72;
export const MIN_THROUGHPUT = 0.05;

export const MAGNETIC_SPEED_MULT = 0.6;

/**
 * Target production band vs consumption for each color:
 * keep surplus between +30% and +50%. Generators stop spawning at the cap.
 */
export const PRODUCTION_SURPLUS_MIN = 1.3;
export const PRODUCTION_SURPLUS_MAX = 1.5;
/** @deprecated Prefer PRODUCTION_SURPLUS_MIN — kept for older call sites/tests. */
export const PRODUCTION_SURPLUS_MULT = PRODUCTION_SURPLUS_MIN;

/** Spawn cadence — halved vs initial design (2× spawn rate). Last band is endless. */
export const SPAWN_INTERVAL_RANGES: readonly {
  minDay: number;
  maxDay: number;
  minSec: number;
  maxSec: number;
}[] = [
  { minDay: 1, maxDay: 7, minSec: 30, maxSec: 45 },
  { minDay: 8, maxDay: 21, minSec: 25, maxSec: 40 },
  { minDay: 22, maxDay: 42, minSec: 17.5, maxSec: 30 },
  { minDay: 43, maxDay: 70, minSec: 12.5, maxSec: 22.5 },
  // Last band: any day ≥ 71 (fallback in nextSpawnDelay covers past maxDay).
  { minDay: 71, maxDay: Number.MAX_SAFE_INTEGER, minSec: 7.5, maxSec: 15 },
];

/** Day when each color becomes available for spawns (index matches introduction order). */
export const COLOR_UNLOCK_DAYS: Record<ColorId, number> = {
  cyan: 1,
  pink: 8,
  yellow: 15,
  green: 22,
  violet: 30,
};

export const REWARD_CABLES = {
  small: 30,
  standard: 20,
  large: 10,
} as const;

export type RewardQuality = keyof typeof REWARD_CABLES;

export const REWARD_QUALITY_PROBABILITIES: Record<RewardQuality, number> = {
  small: 0.4,
  standard: 0.4,
  large: 0.2,
};

export const WEEK1_GUARANTEED_CABLES = 20;

export const FRAGMENT_FORMULA = {
  perWeek: 5,
  scoreSqrtFactor: 0.1,
} as const;

export function computeFragments(weeksCompleted: number, score: number): number {
  return Math.floor(
    FRAGMENT_FORMULA.perWeek * weeksCompleted +
      FRAGMENT_FORMULA.scoreSqrtFactor * Math.sqrt(Math.max(0, score)),
  );
}

export const MAP_UNLOCK_DAYS = {
  core: 0,
  ruins: 28,
  magnetic: 35,
} as const;

export type PermanentUpgradeId =
  | 'optimizedCabling'
  | 'relayReserve'
  | 'improvedTransmission'
  | 'reinforcedCircuits'
  | 'improvedBatteries'
  | 'magneticShielding'
  | 'interferenceReduction';

export interface PermanentUpgradeDef {
  id: PermanentUpgradeId;
  maxLevel: number;
  /** Effect per level (fraction, e.g. 0.05 = +5%). */
  effectPerLevel: number;
  baseCost: number;
  costGrowth: number;
}

export const PERMANENT_UPGRADES: Record<PermanentUpgradeId, PermanentUpgradeDef> = {
  optimizedCabling: {
    id: 'optimizedCabling',
    maxLevel: 5,
    effectPerLevel: 0.05,
    baseCost: 50,
    costGrowth: 1.6,
  },
  relayReserve: {
    id: 'relayReserve',
    maxLevel: 3,
    effectPerLevel: 1,
    baseCost: 80,
    costGrowth: 1.8,
  },
  improvedTransmission: {
    id: 'improvedTransmission',
    maxLevel: 5,
    effectPerLevel: 0.03,
    baseCost: 60,
    costGrowth: 1.55,
  },
  reinforcedCircuits: {
    id: 'reinforcedCircuits',
    maxLevel: 5,
    effectPerLevel: 0.2,
    baseCost: 60,
    costGrowth: 1.55,
  },
  improvedBatteries: {
    id: 'improvedBatteries',
    maxLevel: 5,
    effectPerLevel: 0.03,
    baseCost: 55,
    costGrowth: 1.5,
  },
  magneticShielding: {
    id: 'magneticShielding',
    maxLevel: 5,
    effectPerLevel: 0.08,
    baseCost: 70,
    costGrowth: 1.65,
  },
  interferenceReduction: {
    id: 'interferenceReduction',
    maxLevel: 5,
    effectPerLevel: 0.08,
    baseCost: 70,
    costGrowth: 1.65,
  },
};

export function permanentUpgradeCost(id: PermanentUpgradeId, targetLevel: number): number {
  const def = PERMANENT_UPGRADES[id];
  return Math.floor(def.baseCost * Math.pow(def.costGrowth, targetLevel - 1));
}

/** Cells per second at speed ×1 (along cable center path). ~30% slower than the original 4. */
export const PULSE_BASE_SPEED = 2.8;

/** Max pulses entering a plain cable segment per second at ×1. */
export const CABLE_BASE_CAPACITY = 5;
/** Throughput on segments touching a relay or switch cell. */
export const CABLE_JUNCTION_CAPACITY = 20;

export const SIM_SPEEDS = [1, 2, 3] as const;
export type SimSpeed = (typeof SIM_SPEEDS)[number];

/** Fixed simulation step (seconds of sim time). 60 Hz keeps pulses visually smooth. */
export const FIXED_TIMESTEP = 1 / 60;

/** Generators / relays: 1×1. Bulbs: 1 wide × 2 tall. */
export const GENERATOR_FOOTPRINT = 1;
export const BULB_FOOTPRINT_W = 1;
export const BULB_FOOTPRINT_H = 2;
/** @deprecated Use BULB_FOOTPRINT_H for height checks. */
export const BULB_FOOTPRINT = BULB_FOOTPRINT_H;
export const RELAY_FOOTPRINT = 1;
export const TELEPORTER_FOOTPRINT = 1;
/** @deprecated Prefer kind-specific footprints. */
export const BUILDING_FOOTPRINT = BULB_FOOTPRINT_H;

/** +1 score each time a pulse reaches a matching bulb. */
export const SCORE_PER_PULSE_DELIVERED = 1;
/** Disabled — score is pulse deliveries only. */
export const SCORE_PER_SURVIVAL_SECOND = 0;

/** Run bonus effect magnitudes by reward quality tier. */
export const RUN_BONUS_EFFECTS = {
  small: {
    pulseSpeed: 0.1,
    /** With pulseSpeed: shorter generator interval → +production. */
    generatorProduction: 0.05,
    bulbCapacity: 0.05,
    extraRelays: 1,
    extraSwitches: 1,
    edgeThroughput: 0.05,
  },
  standard: {
    pulseSpeed: 0.1,
    generatorProduction: 0.05,
    globalThroughput: 0.1,
    extraRelays: 2,
    extraSwitches: 2,
    interferenceResist: 0.15,
  },
  large: {
    globalThroughput: 0.2,
    interferenceResist: 0.25,
    expressLine: true,
    digitalBridge: true,
  },
} as const;
